import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SaveIcon from "@mui/icons-material/Save";
import TopNavBar from "../components/TopNavBar";
import { useNavigate, useParams } from "react-router-dom";
import { getAllTaskMetadata } from "../api/listTypes";
import {
  getServicePlanTemplateById,
  syncServicePlanTemplateFutureJobs,
  updateServicePlanTemplate,
} from "../api/servicePlans";

const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

const RECURRENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "every_n_days", label: "Every N days" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const TRIGGER_MODE_OPTIONS = [
  { value: "schedule", label: "Schedule" },
  { value: "condition", label: "Condition" },
  { value: "hybrid", label: "Hybrid" },
];

function toIntOrNull(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function todayDateKey() {
  return new Date().toISOString().slice(0, 10);
}

function weekdayLabel(day) {
  return (
    WEEKDAY_OPTIONS.find((item) => item.value === Number(day))?.label || "?"
  );
}

function buildRowFromTask(task, index) {
  return {
    id: `${task.pk_id || "row"}-${index}`,
    taskId: String(task.task_id || ""),
    taskName: task.task_name || "",
    recurrenceType: task.recurrence_type || "daily",
    intervalN: task.interval_n != null ? String(task.interval_n) : "",
    dayOfMonth: task.day_of_month != null ? String(task.day_of_month) : "",
    daysOfWeek: task.day_of_week != null ? [Number(task.day_of_week)] : [],
    anchorDate: task.anchor_date ? String(task.anchor_date).slice(0, 10) : "",
    triggerMode: task.trigger_mode || "schedule",
    serviceName: task.service_name || "",
    serviceCategoryName: task.service_category_name || "",
    mandatory: Boolean(task.mandatory),
    active: Boolean(task.active),
    executionOrder: Number(task.execution_order || index + 1),
  };
}

function groupWeeklyRows(tasks = []) {
  const grouped = [];
  const weeklyMap = new Map();

  tasks.forEach((task, index) => {
    const row = buildRowFromTask(task, index);
    if (row.recurrenceType !== "weekly") {
      grouped.push(row);
      return;
    }

    const key = [
      row.taskId,
      row.triggerMode,
      row.mandatory ? 1 : 0,
      row.active ? 1 : 0,
      row.anchorDate || "",
      row.executionOrder,
    ].join("|");

    if (!weeklyMap.has(key)) {
      weeklyMap.set(key, row);
      grouped.push(row);
      return;
    }

    const existing = weeklyMap.get(key);
    const nextDays = new Set([
      ...(existing.daysOfWeek || []),
      ...(row.daysOfWeek || []),
    ]);
    existing.daysOfWeek = [...nextDays].sort((a, b) => a - b);
  });

  return grouped.sort(
    (a, b) => Number(a.executionOrder) - Number(b.executionOrder),
  );
}

function normalizeExecutionOrder(rows = []) {
  return rows
    .slice()
    .sort(
      (a, b) => Number(a.executionOrder || 0) - Number(b.executionOrder || 0),
    )
    .map((row, index) => ({
      ...row,
      executionOrder: index + 1,
    }));
}

function formatRecurrenceFromRow(row) {
  if (row.recurrenceType === "every_n_days") {
    return row.intervalN ? `Every ${row.intervalN} day(s)` : "Every N days";
  }
  if (row.recurrenceType === "weekly") {
    const labels = (row.daysOfWeek || []).map(weekdayLabel);
    return labels.length > 0 ? `Weekly (${labels.join(", ")})` : "Weekly";
  }
  if (row.recurrenceType === "monthly") {
    return row.dayOfMonth ? `Monthly (day ${row.dayOfMonth})` : "Monthly";
  }
  return "Daily";
}

function expandRowsForSave(rows = []) {
  const payload = [];
  const sorted = [...rows].sort(
    (a, b) => Number(a.executionOrder || 0) - Number(b.executionOrder || 0),
  );

  sorted.forEach((row, rowIndex) => {
    const rowOrder = toIntOrNull(row.executionOrder) || rowIndex + 1;
    const base = {
      taskId: toIntOrNull(row.taskId),
      recurrenceType: row.recurrenceType,
      intervalN:
        row.recurrenceType === "every_n_days"
          ? toIntOrNull(row.intervalN)
          : null,
      dayOfMonth:
        row.recurrenceType === "monthly" ? toIntOrNull(row.dayOfMonth) : null,
      anchorDate: row.anchorDate || null,
      triggerMode: row.triggerMode || "schedule",
      mandatory: row.mandatory ? 1 : 0,
      active: row.active ? 1 : 0,
      executionOrder: rowOrder,
    };

    if (row.recurrenceType === "weekly") {
      const days = Array.isArray(row.daysOfWeek)
        ? row.daysOfWeek.map((d) => Number(d)).filter((d) => d >= 1 && d <= 7)
        : [];
      days.forEach((day) => {
        payload.push({
          ...base,
          dayOfWeek: day,
        });
      });
      return;
    }

    payload.push({
      ...base,
      dayOfWeek: null,
    });
  });

  return payload;
}

export default function ConfigureServicePlanTemplatePage() {
  const navigate = useNavigate();
  const { templateId } = useParams();

  const [template, setTemplate] = useState(null);
  const [taskMetadata, setTaskMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [needsSync, setNeedsSync] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncUseSpecificDate, setSyncUseSpecificDate] = useState(false);
  const [syncDate, setSyncDate] = useState(todayDateKey());
  const [syncing, setSyncing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [rowEdit, setRowEdit] = useState(null);
  const [dragRowId, setDragRowId] = useState(null);
  const [dropRowId, setDropRowId] = useState(null);
  const dragRowIdRef = useRef(null);

  useEffect(() => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    Promise.all([getServicePlanTemplateById(templateId), getAllTaskMetadata()])
      .then(([data, metadata]) => {
        const loaded = data.template || null;
        console.log("Service plan template tasks:", loaded?.tasks);
        setTemplate(loaded);
        setTaskMetadata(metadata || null);

        if (loaded) {
          setDraft({
            active: Boolean(loaded.active),
            tasks: groupWeeklyRows(loaded.tasks || []),
          });
        }
      })
      .catch((err) => setError(err.message || "Failed to load template"))
      .finally(() => setLoading(false));
  }, [templateId]);

  const taskOptions = useMemo(() => {
    const byService = taskMetadata?.taskMetaData?.grouped?.by_service || {};
    const templateServiceTypeId = template?.service_type_id;
    const map = new Map();

    Object.values(byService).forEach((group) => {
      // Only offer tasks whose service belongs to the template's service type
      // (e.g. remote vs on-site), mixing the two breaks job creation.
      if (
        templateServiceTypeId != null &&
        group?.service_info?.service_type_id !== templateServiceTypeId
      ) {
        return;
      }

      const tasks = Array.isArray(group?.task) ? group.task : [];
      tasks.forEach((task) => {
        if (!map.has(task.pk_id)) {
          map.set(task.pk_id, {
            id: task.pk_id,
            name: task.task_name,
          });
        }
      });
    });

    return [...map.values()].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [taskMetadata, template]);

  const taskNameById = useMemo(() => {
    const map = new Map();
    taskOptions.forEach((opt) => map.set(String(opt.id), opt.name));
    return map;
  }, [taskOptions]);

  const orderedRows = useMemo(() => {
    const rows = draft?.tasks || [];
    return [...rows].sort(
      (a, b) => Number(a.executionOrder || 0) - Number(b.executionOrder || 0),
    );
  }, [draft]);

  const updateDraftTask = (taskId, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((task) =>
          task.id === taskId ? { ...task, ...patch } : task,
        ),
      };
    });
  };

  const removeDraftTask = (taskId) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: normalizeExecutionOrder(
          prev.tasks.filter((task) => task.id !== taskId),
        ),
      };
    });

    if (rowEdit?.rowId === taskId) {
      setRowEdit(null);
    }
  };

  const startEditRow = (rowId, isNew = false) => {
    if (!draft) return;
    if (rowEdit?.rowId && rowEdit.rowId !== rowId) return;

    const row = draft.tasks.find((item) => item.id === rowId);
    if (!row) return;

    setRowEdit({
      rowId,
      isNew,
      backup: isNew
        ? null
        : {
            ...row,
            daysOfWeek: [...(row.daysOfWeek || [])],
          },
    });
    setSaveError(null);
  };

  const addDraftTask = () => {
    if (!draft || rowEdit?.rowId) return;

    const nextId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newTask = {
      id: nextId,
      taskId: "",
      taskName: "",
      recurrenceType: "daily",
      intervalN: "",
      dayOfMonth: "",
      daysOfWeek: [],
      anchorDate: "",
      triggerMode: "schedule",
      mandatory: true,
      active: true,
      executionOrder: (draft.tasks?.length || 0) + 1,
    };

    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: normalizeExecutionOrder([...(prev.tasks || []), newTask]),
      };
    });

    setRowEdit({
      rowId: nextId,
      isNew: true,
      backup: null,
    });
  };

  const moveTaskByDrag = (fromTaskId, toTaskId) => {
    const fromId = String(fromTaskId || "");
    const toId = String(toTaskId || "");

    if (!fromId || !toId || fromId === toId || rowEdit?.rowId) {
      return;
    }
    console.log("From ID:", fromId, "To ID:", toId);

    setDraft((prev) => {
      if (!prev) return prev;

      const sorted = [...prev.tasks].sort(
        (a, b) => Number(a.executionOrder || 0) - Number(b.executionOrder || 0),
      );
      const fromIndex = sorted.findIndex((item) => String(item.id) === fromId);
      const toIndex = sorted.findIndex((item) => String(item.id) === toId);
      if (fromIndex < 0 || toIndex < 0) return prev;

      const next = [...sorted];
      const [dragged] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, dragged);

      const reordered = next.map((item, index) => ({
        ...item,
        executionOrder: index + 1,
      }));

      return {
        ...prev,
        tasks: reordered,
      };
    });
  };

  const validateTaskRow = (row, rowIndex) => {
    if (!row.taskId) return `Task row ${rowIndex}: task is required`;
    if (!row.recurrenceType)
      return `Task row ${rowIndex}: recurrence type is required`;

    if (!taskOptions.some((opt) => String(opt.id) === String(row.taskId))) {
      return `Task row ${rowIndex}: task does not match this template's service type (remote/on-site)`;
    }

    if (row.recurrenceType === "every_n_days" && !toIntOrNull(row.intervalN)) {
      return `Task row ${rowIndex}: interval N is required`;
    }

    if (row.recurrenceType === "monthly") {
      const dom = toIntOrNull(row.dayOfMonth);
      if (!dom || dom < 1 || dom > 31) {
        return `Task row ${rowIndex}: day of month must be 1-31`;
      }
    }

    if (row.recurrenceType === "weekly") {
      const days = Array.isArray(row.daysOfWeek) ? row.daysOfWeek : [];
      if (days.length === 0) {
        return `Task row ${rowIndex}: select at least one weekday`;
      }
    }

    return null;
  };

  const findDuplicateTaskId = (rows = []) => {
    const seen = new Set();
    for (let i = 0; i < rows.length; i += 1) {
      const taskId = String(rows[i]?.taskId || "").trim();
      if (!taskId) continue;
      if (seen.has(taskId)) {
        return taskId;
      }
      seen.add(taskId);
    }
    return null;
  };

  const persistDraft = async () => {
    if (!template || !draft) return;

    const payloadTasks = expandRowsForSave(draft.tasks || []);
    const result = await updateServicePlanTemplate(templateId, {
      active: draft.active ? 1 : 0,
      tasks: payloadTasks,
    });

    const updated = result?.template || null;
    setTemplate(updated);

    if (updated) {
      setDraft({
        active: Boolean(updated.active),
        tasks: groupWeeklyRows(updated.tasks || []),
      });
    }

    setNeedsSync(true);
    setSaveSuccess("Template saved. Sync future jobs to apply changes.");
  };

  const runTemplateSync = async () => {
    if (!templateId) return;

    setSyncing(true);
    setSaveError(null);

    try {
      const payload = {
        effective_from_date: syncUseSpecificDate ? syncDate : todayDateKey(),
      };

      const response = await syncServicePlanTemplateFutureJobs(
        templateId,
        payload,
      );
      const syncedPlans = response?.sync?.synced_plans ?? 0;
      const requestedPlans = response?.sync?.requested_plans ?? 0;

      setNeedsSync(false);
      setSyncDialogOpen(false);
      setSaveSuccess(
        `Future job sync complete (${syncedPlans}/${requestedPlans} plans synced).`,
      );
    } catch (err) {
      setSaveError(err.message || "Failed to sync future jobs");
    } finally {
      setSyncing(false);
    }
  };

  const saveSingleRow = async () => {
    if (!rowEdit?.rowId || !draft) return;

    const row = draft.tasks.find((item) => item.id === rowEdit.rowId);
    const rowIndex =
      orderedRows.findIndex((item) => item.id === rowEdit.rowId) + 1;
    if (!row) return;

    const validationError = validateTaskRow(row, rowIndex || 1);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    const duplicateTaskId = findDuplicateTaskId(draft.tasks || []);
    if (duplicateTaskId) {
      setSaveError(
        `Task #${duplicateTaskId} is selected more than once. Duplicate tasks are not allowed.`,
      );
      return;
    }

    try {
      setSaveError(null);
      setSaving(true);
      await persistDraft();
      setRowEdit(null);
    } catch (err) {
      setSaveError(err.message || "Failed to save task row");
    } finally {
      setSaving(false);
    }
  };

  const cancelSingleRow = () => {
    if (!rowEdit?.rowId || !draft) return;

    if (rowEdit.isNew) {
      removeDraftTask(rowEdit.rowId);
      setRowEdit(null);
      return;
    }

    if (rowEdit.backup) {
      updateDraftTask(rowEdit.rowId, rowEdit.backup);
    }

    setRowEdit(null);
    setSaveError(null);
  };

  const enterEditMode = () => {
    if (!template) return;
    setDraft({
      active: Boolean(template.active),
      tasks: groupWeeklyRows(template.tasks || []),
    });
    setSaveError(null);
    setRowEdit(null);
    setEditMode(true);
    setDragRowId(null);
    setDropRowId(null);
    dragRowIdRef.current = null;
  };

  const leaveEditMode = () => {
    if (!template) return;
    setDraft({
      active: Boolean(template.active),
      tasks: groupWeeklyRows(template.tasks || []),
    });
    setSaveError(null);
    setRowEdit(null);
    setEditMode(false);
    setDragRowId(null);
    setDropRowId(null);
    dragRowIdRef.current = null;
  };

  const saveTemplateStatusOnly = async () => {
    if (!draft || rowEdit?.rowId) return;

    const duplicateTaskId = findDuplicateTaskId(draft.tasks || []);
    if (duplicateTaskId) {
      setSaveError(
        `Task #${duplicateTaskId} is selected more than once. Duplicate tasks are not allowed.`,
      );
      return;
    }

    try {
      setSaveError(null);
      setSaving(true);
      await persistDraft();
    } catch (err) {
      setSaveError(err.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopNavBar heading="Configure Service Plan" />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Tooltip title="Back to service plans">
            <IconButton onClick={() => navigate("/configure-service-plans")}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {template?.template_name || `Template #${templateId}`}
          </Typography>

          {template && (
            <Chip
              label={template.active ? "Active" : "Inactive"}
              size="small"
              color={template.active ? "success" : "default"}
              variant="outlined"
            />
          )}

          {template && (
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              {needsSync && (
                <Button
                  variant="contained"
                  onClick={() => setSyncDialogOpen(true)}
                >
                  Sync Future Jobs
                </Button>
              )}
              {!editMode ? (
                <Button variant="contained" onClick={enterEditMode}>
                  Edit
                </Button>
              ) : (
                <Button onClick={leaveEditMode} color="inherit">
                  Close Edit Mode
                </Button>
              )}
            </Box>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSaveSuccess(null)}
          >
            {saveSuccess}
          </Alert>
        )}

        {!loading && !error && !template && (
          <Alert severity="warning">Service plan template was not found.</Alert>
        )}

        {!loading && !error && template && draft && (
          <Stack spacing={2}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
              >
                <Chip
                  label={`Customer: ${template.customer_name || "General"}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Service type: ${template.service_type_name || "-"}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Version: ${template.version_no ?? 1}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Rows: ${orderedRows.length}`}
                  variant="outlined"
                  size="small"
                />

                {editMode && (
                  <FormControlLabel
                    sx={{ ml: 1 }}
                    control={
                      <Switch
                        checked={Boolean(draft.active)}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  active: e.target.checked,
                                }
                              : prev,
                          )
                        }
                      />
                    }
                    label="Template Active"
                  />
                )}

                {editMode && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={saveTemplateStatusOnly}
                    disabled={saving || Boolean(rowEdit?.rowId)}
                  >
                    Save Template
                  </Button>
                )}
              </Stack>
            </Paper>

            {editMode && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
                  Tasks
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addDraftTask}
                  disabled={Boolean(rowEdit?.rowId)}
                >
                  Add Task
                </Button>
              </Box>
            )}

            {editMode && !rowEdit?.rowId && (
              <Typography variant="caption" color="text.secondary">
                Drag and drop a row to reorder tasks.
              </Typography>
            )}

            {orderedRows.map((row, idx) => {
              const isEditing = rowEdit?.rowId === row.id;

              return (
                <Paper
                  key={row.id}
                  variant="outlined"
                  draggable={editMode && !Boolean(rowEdit?.rowId)}
                  onDragStart={(e) => {
                    if (!editMode || rowEdit?.rowId) return;
                    const id = String(row.id);
                    dragRowIdRef.current = id;
                    setDragRowId(id);
                    e.dataTransfer.setData("text/plain", id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    if (!editMode || rowEdit?.rowId) return;
                    e.preventDefault();
                    if (dropRowId !== row.id) {
                      setDropRowId(row.id);
                    }
                  }}
                  onDragLeave={() => {
                    if (dropRowId === row.id) {
                      setDropRowId(null);
                    }
                  }}
                  onDrop={(e) => {
                    if (!editMode || rowEdit?.rowId) return;
                    e.preventDefault();
                    const sourceId =
                      dragRowIdRef.current ||
                      e.dataTransfer.getData("text/plain") ||
                      dragRowId;
                    moveTaskByDrag(sourceId, row.id);
                    setDragRowId(null);
                    setDropRowId(null);
                    dragRowIdRef.current = null;
                  }}
                  onDragEnd={() => {
                    setDragRowId(null);
                    setDropRowId(null);
                    dragRowIdRef.current = null;
                  }}
                  sx={{
                    p: isEditing ? 2 : 1,
                    borderRadius: 2,
                    borderColor:
                      editMode && dropRowId === row.id
                        ? "primary.main"
                        : "divider",
                    cursor:
                      editMode && !Boolean(rowEdit?.rowId) ? "grab" : "default",
                    opacity: dragRowId === String(row.id) ? 0.75 : 1,
                  }}
                >
                  {!isEditing && (
                    <Stack spacing={0.5}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Chip size="small" label={`#${row.executionOrder}`} />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, lineHeight: 1.2 }}
                        >
                          {taskNameById.get(String(row.taskId)) ||
                            row.taskName ||
                            "Unselected task"}
                        </Typography>
                        <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
                          {editMode && (
                            <>
                              <IconButton
                                size="small"
                                draggable={false}
                                disabled={Boolean(rowEdit?.rowId)}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => startEditRow(row.id, false)}
                                disabled={Boolean(rowEdit?.rowId)}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => removeDraftTask(row.id)}
                                disabled={Boolean(rowEdit?.rowId)}
                              >
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={formatRecurrenceFromRow(row)}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={`Trigger: ${row.triggerMode || "schedule"}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={`Type:${row.serviceName || "-"} `}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={`Category:${row.serviceCategoryName || "-"} `}
                        />
                        {/*  <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={row.mandatory ? "Mandatory" : "Optional"}
                        /> */}
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                          label={row.active ? "Active" : "Inactive"}
                        />
                      </Stack>
                    </Stack>
                  )}

                  {isEditing && (
                    <Stack spacing={2}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          size="small"
                          label={`Editing row #${row.executionOrder}`}
                        />
                        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            color="inherit"
                            onClick={cancelSingleRow}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={saveSingleRow}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save Row"}
                          </Button>
                        </Box>
                      </Box>

                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          select
                          fullWidth
                          label="Task"
                          value={row.taskId}
                          onChange={(e) =>
                            updateDraftTask(row.id, {
                              taskId: e.target.value,
                              taskName:
                                taskOptions.find(
                                  (opt) =>
                                    String(opt.id) === String(e.target.value),
                                )?.name || "",
                            })
                          }
                        >
                          <MenuItem value="">Select Task</MenuItem>
                          {taskOptions.map((opt) => {
                            const selectedByAnotherRow = orderedRows.some(
                              (existing) =>
                                existing.id !== row.id &&
                                String(existing.taskId || "") ===
                                  String(opt.id),
                            );

                            return (
                              <MenuItem
                                key={opt.id}
                                value={opt.id}
                                disabled={selectedByAnotherRow}
                              >
                                {opt.name}
                              </MenuItem>
                            );
                          })}
                        </TextField>

                        <TextField
                          select
                          fullWidth
                          label="Recurrence"
                          value={row.recurrenceType}
                          onChange={(e) =>
                            updateDraftTask(row.id, {
                              recurrenceType: e.target.value,
                              intervalN:
                                e.target.value === "every_n_days"
                                  ? row.intervalN
                                  : "",
                              dayOfMonth:
                                e.target.value === "monthly"
                                  ? row.dayOfMonth
                                  : "",
                              daysOfWeek:
                                e.target.value === "weekly"
                                  ? row.daysOfWeek
                                  : [],
                            })
                          }
                        >
                          {RECURRENCE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          disabled
                          fullWidth
                          label="Trigger"
                          value={row.triggerMode}
                          onChange={(e) =>
                            updateDraftTask(row.id, {
                              triggerMode: e.target.value,
                            })
                          }
                        >
                          {TRIGGER_MODE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      {row.recurrenceType === "every_n_days" && (
                        <TextField
                          label="Interval N (days)"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={row.intervalN}
                          onChange={(e) =>
                            updateDraftTask(row.id, {
                              intervalN: e.target.value,
                            })
                          }
                        />
                      )}

                      {row.recurrenceType === "monthly" && (
                        <TextField
                          label="Day of month"
                          type="number"
                          inputProps={{ min: 1, max: 31 }}
                          value={row.dayOfMonth}
                          onChange={(e) =>
                            updateDraftTask(row.id, {
                              dayOfMonth: e.target.value,
                            })
                          }
                        />
                      )}

                      {row.recurrenceType === "weekly" && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            Weekdays
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {WEEKDAY_OPTIONS.map((day) => {
                              const checked = (row.daysOfWeek || []).includes(
                                day.value,
                              );
                              return (
                                <FormControlLabel
                                  key={day.value}
                                  sx={{ mr: 1 }}
                                  control={
                                    <Checkbox
                                      checked={checked}
                                      icon={<RadioButtonUncheckedIcon />}
                                      checkedIcon={<CheckCircleIcon />}
                                      onChange={(e) => {
                                        const set = new Set(
                                          row.daysOfWeek || [],
                                        );
                                        if (e.target.checked)
                                          set.add(day.value);
                                        else set.delete(day.value);
                                        updateDraftTask(row.id, {
                                          daysOfWeek: [...set].sort(
                                            (a, b) => a - b,
                                          ),
                                        });
                                      }}
                                    />
                                  }
                                  label={day.label}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      <TextField
                        type="date"
                        label="Anchor date (optional)"
                        InputLabelProps={{ shrink: true }}
                        value={row.anchorDate || ""}
                        onChange={(e) =>
                          updateDraftTask(row.id, {
                            anchorDate: e.target.value,
                          })
                        }
                      />

                      <Stack direction="row" spacing={2}>
                        <FormControlLabel
                          disabled
                          control={
                            <Switch
                              checked={Boolean(row.mandatory)}
                              onChange={(e) =>
                                updateDraftTask(row.id, {
                                  mandatory: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Mandatory"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(row.active)}
                              onChange={(e) =>
                                updateDraftTask(row.id, {
                                  active: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Active"
                        />
                      </Stack>
                    </Stack>
                  )}
                </Paper>
              );
            })}

            {orderedRows.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography color="text.secondary">
                  No tasks configured.
                </Typography>
              </Paper>
            )}
          </Stack>
        )}

        <Dialog
          open={syncDialogOpen}
          onClose={() => (!syncing ? setSyncDialogOpen(false) : null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Sync Existing Future Jobs</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Apply template changes to existing future jobs from today or a
                specific date.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncUseSpecificDate}
                    onChange={(event) =>
                      setSyncUseSpecificDate(event.target.checked)
                    }
                  />
                }
                label="Use specific effective date"
              />
              {syncUseSpecificDate && (
                <TextField
                  label="Effective from"
                  type="date"
                  value={syncDate}
                  onChange={(event) => setSyncDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={runTemplateSync}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "Sync Now"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
